// OnboardingTour.jsx — Triggered on first login for trial accounts, new org members, or from kNine AI chat
import React, { useState } from 'react';
import { Search, Target, Building2, MessageSquare, Sparkles, Crown, Database, X, ChevronRight, ChevronLeft } from 'lucide-react';

const TOUR_STEPS = [
  {
    icon: Sparkles,
    gradient: 'linear-gradient(135deg, #38bdf8, #818cf8)',
    title: 'Welcome to kNine!',
    subtitle: "Your kNine AI platform — quick orientation",
    body: "Let me give you a quick tour. In just a few steps you'll see how to search 100M+ candidates with natural language, discover companies for business development, and get the most out of your trial.",
    tip: null,
    badge: null,
    cta: "Let's go",
  },
  {
    icon: Search,
    gradient: 'linear-gradient(135deg, #818cf8, #6366f1)',
    title: 'Roxi — AI Candidate Search',
    subtitle: 'Search 100M+ professionals in plain English',
    body: 'Just describe who you\'re looking for — "Senior Java dev in Austin with fintech experience" — and Roxi finds, ranks, and scores candidates using AI. No Boolean required.',
    tip: 'On your trial, Roxi searches the kNine network. Connect your ATS on a paid plan to blend your own database into every result, weighted and ranked together with AI.',
    badge: { type: 'lock', label: 'kNine records only — ATS database locked on trial' },
    cta: 'Next',
  },
  {
    icon: Target,
    gradient: 'linear-gradient(135deg, #f59e0b, #f97316)',
    title: 'Cooper — BD Company Search',
    subtitle: 'AI-powered business development',
    body: 'Cooper discovers and prioritises companies for business development. Search by industry, size, location and sales approach, then get ranked contact recommendations from 100M+ records.',
    tip: 'On your trial, Cooper searches the kNine network. Upgrade to source over 100M records alongside your own ATS contacts — all weighted and ranked with AI and simple language searching.',
    badge: { type: 'lock', label: 'kNine records only — ATS blending locked on trial' },
    cta: 'Next',
  },
  {
    icon: Building2,
    gradient: 'linear-gradient(135deg, #10b981, #0ea5e9)',
    title: 'Connect Your ATS',
    subtitle: 'PCRecruiter, Bullhorn, TE Recruit & more',
    body: 'Upgrade your account to connect your database and source over 100M kNine records alongside your own candidates — all weighted and ranked together by AI with simple, natural language searching.',
    tip: null,
    badge: { type: 'upgrade', label: 'Upgrade your account to unlock ATS integrations' },
    cta: 'Next',
  },
  {
    icon: MessageSquare,
    gradient: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
    title: 'Ask kNine AI Anything',
    subtitle: 'Your always-on AI assistant',
    body: 'The kNine AI button lives in the bottom-right corner on every page. Ask for search help, candidate insights, outreach tips — or type "show me around" at any time to restart this tour.',
    tip: null,
    badge: null,
    cta: 'Start exploring',
  },
];

/**
 * OnboardingTour
 *
 * Shown when:
 *   - Trial/free account on first login  (checked by MainLayout)
 *   - New org member on first login       (checked by MainLayout)
 *   - User types "show me around" / "take a tour" in kNine AI chat
 *
 * On dismiss: sets localStorage key 'knine_tour_seen' = 'true'
 * Re-trigger:  set localStorage key 'knine_tour_retrigger' = 'true' from anywhere
 */
export default function OnboardingTour({ onDismiss }) {
  const [step, setStep] = useState(0);
  const [leaving, setLeaving] = useState(false);

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;

  // Per-user tour key so each account gets its own first-login tour
  const tourKey = (() => {
    try {
      const uid = JSON.parse(localStorage.getItem('user') || '{}')?.id;
      return uid ? `knine_tour_seen_${uid}` : 'knine_tour_seen';
    } catch { return 'knine_tour_seen'; }
  })();

  const close = () => {
    setLeaving(true);
    setTimeout(() => {
      localStorage.setItem(tourKey, 'true');
      onDismiss();
    }, 250);
  };

  const next = () => (isLast ? close() : setStep(s => s + 1));
  const back = () => setStep(s => s - 1);

  const Icon = current.icon;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9100,
      background: 'rgba(0,0,0,0.78)',
      backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      opacity: leaving ? 0 : 1,
      transition: 'opacity 0.25s',
    }}>
      <style>{`
        @keyframes knine-tour-up {
          from { opacity: 0; transform: translateY(28px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div className="kubi-modal-card" style={{
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 20,
        width: '100%', maxWidth: 480,
        margin: 20,
        boxShadow: '0 40px 80px rgba(0,0,0,0.55)',
        overflow: 'hidden',
        animation: 'knine-tour-up 0.3s ease',
      }}>
        {/* Colour accent strip */}
        <div style={{ height: 4, background: current.gradient }} />

        <div style={{ padding: '28px 28px 20px' }}>
          {/* Skip link */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
            <button onClick={close} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#64748b', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <X size={15} /> Skip tour
            </button>
          </div>

          {/* Step icon */}
          <div style={{
            width: 60, height: 60, borderRadius: 16,
            background: current.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 20,
          }}>
            <Icon size={28} color="#fff" />
          </div>

          {/* Step label */}
          <div style={{ fontSize: 10, letterSpacing: '1.2px', textTransform: 'uppercase', color: '#64748b', marginBottom: 6 }}>
            Step {step + 1} of {TOUR_STEPS.length}
          </div>

          <h2 style={{ margin: '0 0 4px', fontSize: 21, fontWeight: 800, color: '#f8fafc' }}>
            {current.title}
          </h2>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#818cf8', marginBottom: 12 }}>
            {current.subtitle}
          </div>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: '#94a3b8', lineHeight: 1.65 }}>
            {current.body}
          </p>

          {/* Trial lock badge */}
          {current.badge?.type === 'lock' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.25)',
              marginBottom: 14,
            }}>
              <Database size={15} style={{ color: '#f59e0b', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#f59e0b' }}>{current.badge.label}</span>
            </div>
          )}

          {/* Upgrade badge */}
          {current.badge?.type === 'upgrade' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: 'rgba(139,92,246,0.1)',
              border: '1px solid rgba(139,92,246,0.25)',
              marginBottom: 14,
            }}>
              <Crown size={15} style={{ color: '#8b5cf6', flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: '#8b5cf6' }}>{current.badge.label}</span>
            </div>
          )}

          {/* Contextual tip */}
          {current.tip && (
            <div style={{
              padding: '10px 14px', borderRadius: 10,
              background: 'rgba(56,189,248,0.08)',
              border: '1px solid rgba(56,189,248,0.15)',
              marginBottom: 14,
            }}>
              <span style={{ fontSize: 13, color: '#7dd3fc', lineHeight: 1.55 }}>
                💡 {current.tip}
              </span>
            </div>
          )}
        </div>

        {/* Footer — dots + nav */}
        <div style={{ padding: '14px 28px 26px', display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Progress dots (clickable) */}
          <div style={{ display: 'flex', gap: 6, flex: 1 }}>
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                onClick={() => setStep(i)}
                style={{
                  height: 7,
                  width: i === step ? 22 : 7,
                  borderRadius: 4,
                  background: i === step ? '#818cf8' : '#334155',
                  cursor: 'pointer',
                  transition: 'all 0.25s',
                }}
              />
            ))}
          </div>

          {step > 0 && (
            <button onClick={back} style={{
              padding: '9px 14px', borderRadius: 10,
              border: '1px solid #334155', background: 'transparent',
              color: '#94a3b8', cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <ChevronLeft size={15} /> Back
            </button>
          )}

          <button onClick={next} style={{
            padding: '9px 18px', borderRadius: 10,
            border: 'none', background: current.gradient,
            color: '#fff', cursor: 'pointer',
            fontSize: 14, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            {current.cta} {!isLast && <ChevronRight size={15} />}
          </button>
        </div>
      </div>
    </div>
  );
}
